import { useState } from 'react';
import { Helmet } from 'react-helmet';
import type { VoidFunctionComponent } from 'react';

import { BINARY_DECOMPOSITION, GLOBAL_LICENSE_CORRECTIONS } from '../../../shared/featureFlags/flagNames';
import { PREMIUM_PER_REVISION_PLAN_NAME } from '../../../shared/subscriptions';
import { window } from '../../globals';
import { useIsFeatureEnabled } from '../../helpers/features';
import CallToAction from '../CallToAction';
import { FeatureFlagRequired } from '../generic';
import Flex from '../generic/Flex';
import PageHeadingBreadcrumbs from '../generic/PageHeadingBreadcrumbs';
import type { Organization } from '../../types';

import { BackfillIssueSanitizedLocators } from './BackfillIssueSanitizedLocators';
import BackfillLicenseIdentificationDataCard from './BackfillLicenseIdentificationDataCard';
import BackfillSBOMSummaryData from './BackfillSBOMSummaryData';
import BillingTermCard from './BillingTermCard';
import ChangeOrgComponent from './ChangeOrgCard';
import ChangeSubcriptionComponent from './ChangeSubscriptionCard';
import ContributorsCard from './ContributorsCard';
import ContributorCountComponent from './CustomContribCountCard';
import CustomProvidedBuildsQueueCard from './CustomProvidedBuildsQueueCard';
import DeleteAllProjectsCard from './DeleteAllProjectsCard';
import FeatureControlCard from './FeatureControlCard';
import { FullOrganizationPackageCacheRebuildCard } from './FullOrganizationPackageCacheRebuildCard';
import GithubOrgComponent from './GithubOrgComponent';
import IssueCountBackfillCard from './IssueCountBackfillCard';
import LicenseCorrectionCard from './LicenseCorrectionCard/LicenseCorrectionCard';
import OrgInfoCard from './OrgInfoCard';
import ReanalyzeQuickImportCard from './ReanalyzeQuickImportCard';
import ToggleInternalOrgCard from './ToggleInternalOrgCard/ToggleInternalOrgCard';
import UserInfoComponent from './UserInfoCard';

const AdminDashboard: VoidFunctionComponent = () => {
  const [org, setOrg] = useState<Organization>(window.user?.organization as Organization);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const hasBinaryDecomp = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  return (
    <>
      <CallToAction>
        <PageHeadingBreadcrumbs
          truncateAt={80}
          crumbs={[
            {
              to: '/admin',
              text: 'Admin Tools',
            },
            {
              text: window.user?.organization?.title ?? 'Organization',
            },
          ]}
        />
      </CallToAction>
      <div className="container margin-top admin-dashboard">
        <Helmet title="Admin Dashboard" />
        {generalError ? <div className="alert alert-error">{generalError}</div> : null}
        <div className="alert">
          Stuff on this page can have catastrophic effects if used incorrectly. Make sure you know what
          you&#39;re doing.
        </div>
        <div className="content">
          <div className="content--main">
            <ChangeOrgComponent />
            <ChangeSubcriptionComponent org={org} />
            {(org.stripe_plan === PREMIUM_PER_REVISION_PLAN_NAME || hasBinaryDecomp) &&
              <BillingTermCard />}
            <ContributorCountComponent org={org} setOrg={setOrg} />
            <FeatureControlCard setError={setGeneralError} />
            <GithubOrgComponent />
            <FeatureFlagRequired flagName={GLOBAL_LICENSE_CORRECTIONS}>
              <LicenseCorrectionCard />
            </FeatureFlagRequired>
            {!window.on_prem && <CustomProvidedBuildsQueueCard />}
            <ReanalyzeQuickImportCard />
            <FullOrganizationPackageCacheRebuildCard org={org} />
            <BackfillLicenseIdentificationDataCard />
            <BackfillSBOMSummaryData />
            <BackfillIssueSanitizedLocators />
            {/* These cards use newer generics and styles, so we need to space them out with a flex. */}
            <Flex direction="column" grow={1} align="flex-start" gap={25}>
              <ToggleInternalOrgCard />
              <IssueCountBackfillCard />
              <DeleteAllProjectsCard />
            </Flex>
          </div>
          <div className="content--side">
            <UserInfoComponent />
            <OrgInfoCard />
            <ContributorsCard />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
